import type { Node as PMNode } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/core'
import { pmHeadings } from './pm-sections'

/**
 * The LOCAL find/replace scopes ("this section" / "this part") — search the live editor's
 * RENDERED text (ProseMirror position space), never the raw markdown Y.Text. That is the
 * opposite choice from the WIDE scopes (lib/wide-replace.ts, which has no editor to search), and
 * deliberately so: a match found here can be selected/highlighted and replaced through a real
 * transaction, which is what lets it reuse the editor's existing machinery wholesale — tiptap's
 * markdown re-serialise → `syncYText` diff on `onUpdate`, AND `SectionNumbers.filterTransaction`'s
 * § lock enforcement — rather than re-implementing either one here.
 *
 * The cost of that choice: matching walks each TEXT NODE independently, so a query that straddles
 * a mark boundary (e.g. half in **bold**, half not) will not be found — the common case (a plain
 * run of prose) always is. A full cross-node search would need a flattened-text ⇄ position map,
 * which is real complexity for a case this feature does not need to solve; see
 * lib/find-replace.ts's header for the analogous, and equally deliberate, gap on the wide scopes.
 */

/** One occurrence, as a ProseMirror position range. */
export interface PmMatch {
  from: number
  to: number
}

/** Every occurrence of `query` in `doc`'s rendered text, left to right, text-node by text-node. */
export function findInDoc(doc: PMNode, query: string, caseSensitive: boolean): PmMatch[] {
  if (query === '') return []
  const needle = caseSensitive ? query : query.toLowerCase()
  const matches: PmMatch[] = []
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return
    const hay = caseSensitive ? node.text : node.text.toLowerCase()
    let from = 0
    while (from <= hay.length - needle.length) {
      const idx = hay.indexOf(needle, from)
      if (idx === -1) break
      matches.push({ from: pos + idx, to: pos + idx + needle.length })
      from = idx + needle.length
    }
  })
  return matches
}

/** Matches wholly inside `[from, to)` — the "this section" sub-scope of a search already run
 *  over the whole doc. */
export function withinRange(matches: PmMatch[], from: number, to: number): PmMatch[] {
  return matches.filter((m) => m.from >= from && m.to <= to)
}

/**
 * The PM range of the § the given position sits in: the last heading at or before `pos`, through
 * its whole subtree (`pmHeadings`' definition of a §) — or the preamble before the first heading
 * when `pos` sits above every one. `null` when the document has no heading at all, meaning there
 * is no § to scope to; the caller falls back to treating the whole part as the range.
 */
export function currentSectionRange(doc: PMNode, pos: number): { from: number; to: number } | null {
  const heads = pmHeadings(doc)
  if (heads.length === 0) return null
  let range = { from: 0, to: heads[0]!.from }
  for (const h of heads) {
    if (pos >= h.from) range = { from: h.from, to: h.to }
  }
  return range
}

/**
 * Replace every match with `replacement`, as ONE transaction dispatched through the editor —
 * never straight to the Y.Text. Going through the editor is what makes this reuse, rather than
 * duplicate:
 *  - tiptap-markdown's re-serialise → `syncYText`'s diff, on the resulting `onUpdate` (RichEditor.tsx);
 *  - `SectionNumbers.filterTransaction`'s § lock enforcement, which vets every step of the SAME
 *    transaction and rejects the whole thing (no partial replace) if any of them lands in a
 *    section someone else holds.
 *
 * Matches are applied back-to-front: `from`/`to` for every match were computed against the SAME
 * original `doc`, and replacing text strictly to the RIGHT of a position never changes that
 * position's validity, so processing rightmost-first needs no position remapping.
 */
export function replaceMatchesInEditor(editor: Editor, matches: PmMatch[], replacement: string): void {
  if (matches.length === 0) return
  editor
    .chain()
    .command(({ tr, dispatch }) => {
      for (let i = matches.length - 1; i >= 0; i--) {
        const m = matches[i]!
        tr.insertText(replacement, m.from, m.to)
      }
      if (dispatch) dispatch(tr)
      return true
    })
    .run()
}
